import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'superadmin' | 'manager' | 'sales';
  shopId: string;
  shopName: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}/auth`;

  private _currentUser = signal<User | null>(null);
  private _token = signal<string | null>(null);
  private _refreshToken = signal<string | null>(null);

  currentUser = this._currentUser.asReadonly();
  token = this._token.asReadonly();
  refreshToken = this._refreshToken.asReadonly();

  isAuthenticated = computed(() => !!this._token());
  isSuperAdmin = computed(() => this._currentUser()?.role === 'superadmin');
  isAdmin = computed(() => {
    const user = this._currentUser();
    return user?.role === 'manager';
  });

  constructor(private http: HttpClient, private router: Router) {
    this.loadFromStorage();
  }

  login(email: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, { email, password }).pipe(
      tap((response) => {
        this.setSession(response);
      })
    );
  }

  demoLogin(role: 'sales' | 'manager' | 'owner'): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/demo-login`, { role }).pipe(
      tap((response) => {
        this.setSession(response);
      })
    );
  }

  refreshSession(): Observable<AuthResponse> {
    const refreshToken = this._refreshToken();
    return this.http.post<AuthResponse>(`${this.apiUrl}/refresh`, { refreshToken }).pipe(
      tap((response) => {
        this.setSession(response);
      })
    );
  }

  logout(sessionExpired = false): void {
    this._token.set(null);
    this._refreshToken.set(null);
    this._currentUser.set(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    if (sessionExpired) {
      this.router.navigate(['/login'], { queryParams: { sessionExpired: 'true' } });
    } else {
      this.router.navigate(['/login']);
    }
  }

  isTokenExpired(): boolean {
    const token = this._token();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Add 10 second buffer to prevent edge cases
      return payload.exp * 1000 < Date.now() + 10000;
    } catch {
      return true;
    }
  }

  hasRefreshToken(): boolean {
    return !!this._refreshToken();
  }

  updateTokens(accessToken: string, refreshToken: string): void {
    this._token.set(accessToken);
    this._refreshToken.set(refreshToken);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  private setSession(response: AuthResponse): void {
    this._token.set(response.accessToken);
    this._refreshToken.set(response.refreshToken);
    this._currentUser.set(response.user);
    localStorage.setItem('token', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('user', JSON.stringify(response.user));
  }

  private loadFromStorage(): void {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const userJson = localStorage.getItem('user');

    if (token && userJson) {
      this._token.set(token);
      this._currentUser.set(JSON.parse(userJson));
    }

    if (refreshToken) {
      this._refreshToken.set(refreshToken);
    }
  }
}
